import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Swal from 'sweetalert2';
import { enquiry } from '../../../../helper/api_url';
import { swal } from '../../../../helper/swal';
import { setPreloader } from '../../../../features/Ui/uiSlice';
import { getEnquiryEffectiveStatus, getUniqueDistricts } from '../EnquiryList/enquiryUtils';

export const useStatusEnquiry = (targetStatus) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const authData = useSelector((state) => state.auth._user);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDistrict, setSelectedDistrict] = useState('all');

    // Fetch all enquiries
    const {
        data: allEnquiries = [],
        isLoading,
        isError,
        error
    } = useQuery({
        queryKey: ['enquirys'],
        queryFn: enquiry.list,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        select: (data) => data?.data?.enquiry || []
    });

    useEffect(() => {
        if (isError && error) {
            swal.error(error.response?.data?.message || error.message || 'Failed to load enquiries');
        }
    }, [isError, error]);

    // Filter by target status (e.g. 'completed' or 'deleted')
    const filteredByStatus = useMemo(() => {
        return allEnquiries.filter(
            (item) => getEnquiryEffectiveStatus(item) === targetStatus
        );
    }, [allEnquiries, targetStatus]);

    // Extract unique sorted districts
    const districtList = useMemo(() => {
        return getUniqueDistricts(filteredByStatus);
    }, [filteredByStatus]);

    // Further filter by district and search term
    const displayedEnquiries = useMemo(() => {
        return filteredByStatus.filter((item) => {
            if (selectedDistrict !== 'all') {
                const itemDist = (item.district || '').trim().toLowerCase();
                if (itemDist !== selectedDistrict.toLowerCase()) return false;
            }

            if (searchTerm.trim()) {
                const term = searchTerm.toLowerCase();
                const name = (item.name || '').toLowerCase();
                const phone = item.phone || '';
                const principal = (item.father_name || '').toLowerCase();
                const district = (item.district || '').toLowerCase();
                return name.includes(term) || phone.includes(term) || principal.includes(term) || district.includes(term);
            }
            return true;
        });
    }, [filteredByStatus, selectedDistrict, searchTerm]);

    // Handle delete with confirmation
    const handleEnquiryDelete = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this enquiry!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(setPreloader({ loader: true, message: 'Deleting enquiry, please wait...' }));
                enquiry
                    .delete(id)
                    .then(() => {
                        queryClient.invalidateQueries(['enquirys']);
                        swal.success('Enquiry deleted successfully');
                    })
                    .catch((err) => {
                        swal.error(err.response?.data?.message || err.message || 'Failed to delete enquiry');
                    })
                    .finally(() => {
                        dispatch(setPreloader({ loader: false, message: '' }));
                    });
            }
        });
    };

    const handleReport = (data) => {
        navigate('/report', { state: { data } });
    };

    return {
        enquirys: displayedEnquiries,
        totalCount: filteredByStatus.length,
        districtList,
        selectedDistrict,
        setSelectedDistrict,
        isLoading,
        authData,
        searchTerm,
        setSearchTerm,
        handleEnquiryDelete,
        handleReport
    };
};

export default useStatusEnquiry;
